/*
Psychological persistence model:
- Audio does not truly persist across page loads.
- User intent and UI state are restored from localStorage.
- Playback resumes only if previously user initiated and permitted.
- Playback position is not stored. Ambient audio restarts naturally.
*/

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    NAMESPACE: 'sl_ambient_',
    STORAGE_KEYS: {
      TRACK_ID: 'track_id',
      TRACK_TITLE: 'track_title',
      TRACK_URL: 'track_url',
      TRACK_LOCATION_LABEL: 'track_location_label',
      TRACK_LOCATION_VALUE: 'track_location_value',
      VOLUME: 'volume',
      IS_PLAYING: 'is_playing',
      USER_INITIATED: 'user_initiated'
    },
    SELECTORS: {
      CONTAINER: '[data-ambient-player-bar]',
      CONTAINER_TOGGLE: '.ambient-player-bar__container--toggle',
      AUDIO: '[data-audio-element]',
      PLAY_PAUSE_BTN: '[data-play-pause-btn]',
      ICON_PLAY: '.ambient-player-bar__icon-play',
      ICON_PAUSE: '.ambient-player-bar__icon-pause',
      TRACK_TITLE: '[data-track-title]',
      AUDIO_TRIGGER: '[data-audio-menu-trigger]',
      AUDIO_DROPDOWN: '[data-audio-dropdown]',
      TRACK_OPTION: '[data-track-option]',
      INFO_TRIGGER: '[data-info-caret]',
      INFO_PANEL: '[data-info-panel]',
      INFO_CONTENT_CONTAINER: '[data-info-content-container]',
      LOCATION_CONTAINER: '[data-location-container]',
      LOCATION_LABEL: '[data-location-label]',
      LOCATION_VALUE: '[data-location-value]',
      VOLUME_TOGGLE: '[data-volume-toggle]',
      VOLUME_TRIGGER: '[data-volume-trigger]',
      VOLUME_PANEL: '[data-volume-panel]',
      VOLUME_SLIDER: '[data-volume-slider]',
      VOLUME_ICON_ON: '.ambient-player-bar__volume-icon--on',
      VOLUME_ICON_OFF: '.ambient-player-bar__volume-icon--off',
      TOGGLE_TRACK: '[data-toggle-track]',
      TOGGLE_PLAY: '[data-toggle-play]'
    },
    DEFAULT_VOLUME: 0.7
  };

  // State Management
  const StateManager = {
    get(key) {
      try {
        const item = localStorage.getItem(CONFIG.NAMESPACE + key);
        return item ? JSON.parse(item) : null;
      } catch (e) {
        console.warn('Ambient Player: LocalStorage access denied', e);
        return null;
      }
    },

    set(key, value) {
      try {
        localStorage.setItem(CONFIG.NAMESPACE + key, JSON.stringify(value));
      } catch (e) {
        console.warn('Ambient Player: LocalStorage write failed', e);
      }
    },

    getAll() {
      return {
        trackId: this.get(CONFIG.STORAGE_KEYS.TRACK_ID),
        trackTitle: this.get(CONFIG.STORAGE_KEYS.TRACK_TITLE),
        trackUrl: this.get(CONFIG.STORAGE_KEYS.TRACK_URL),
        trackLocationLabel: this.get(CONFIG.STORAGE_KEYS.TRACK_LOCATION_LABEL),
        trackLocationValue: this.get(CONFIG.STORAGE_KEYS.TRACK_LOCATION_VALUE),
        volume: this.get(CONFIG.STORAGE_KEYS.VOLUME) || CONFIG.DEFAULT_VOLUME,
        isPlaying: this.get(CONFIG.STORAGE_KEYS.IS_PLAYING) === true,
        userInitiated: this.get(CONFIG.STORAGE_KEYS.USER_INITIATED) === true
      };
    }
  };

  // Audio Controller
  const AudioController = {
    audio: null,
    
    init(audioElement) {
      this.audio = audioElement;
      this.audio.volume = StateManager.get(CONFIG.STORAGE_KEYS.VOLUME) || CONFIG.DEFAULT_VOLUME;
      
      this.bindEvents();
    },

    bindEvents() {
      if (!this.audio) return;

      this.audio.addEventListener('ended', () => {
        this.audio.currentTime = 0;
        this.play();
      });

      this.audio.addEventListener('error', (e) => {
        console.warn('Ambient Player: Audio error', e);
        UIController.updatePlayState(false);
      });
      
      this.audio.addEventListener('play', () => {
        UIController.updatePlayState(true);
        StateManager.set(CONFIG.STORAGE_KEYS.IS_PLAYING, true);
      });

      this.audio.addEventListener('pause', () => {
        UIController.updatePlayState(false);
        StateManager.set(CONFIG.STORAGE_KEYS.IS_PLAYING, false);
      });
    },

    load(url) {
      if (!this.audio) return;
      if (this.audio.src !== url) {
        this.audio.src = url;
        this.audio.load();
      }
    },

    async play() {
      if (!this.audio) return;
      try {
        await this.audio.play();
        UIController.updatePlayState(true);
      } catch (err) {
        // Autoplay blocked or interaction required
        console.log('Ambient Player: Autoplay prevented', err);
        UIController.updatePlayState(false);
        StateManager.set(CONFIG.STORAGE_KEYS.IS_PLAYING, false);
      }
    },

    playFromRandomPosition() {
      if (!this.audio) return;
      if (this.audio.readyState >= 1 && this.audio.duration) {
        this.audio.currentTime = Math.random() * this.audio.duration;
        this.play();
      } else {
        // preload="none" means loadedmetadata won't fire until play() is called.
        // Kick off playback first, then seek once duration is known.
        const onMeta = () => {
          this.audio.removeEventListener('loadedmetadata', onMeta);
          if (this.audio.duration) {
            this.audio.currentTime = Math.random() * this.audio.duration;
          }
        };
        this.audio.addEventListener('loadedmetadata', onMeta);
        this.play();
      }
    },

    pause() {
      if (!this.audio) return;
      this.audio.pause();
      UIController.updatePlayState(false);
    },

    toggle() {
      if (!this.audio) return;
      if (this.audio.paused) {
        this.play();
        StateManager.set(CONFIG.STORAGE_KEYS.USER_INITIATED, true);
      } else {
        this.pause();
      }
    },

    setVolume(value) {
      if (!this.audio) return;
      this.audio.volume = Math.max(0, Math.min(1, value));
      StateManager.set(CONFIG.STORAGE_KEYS.VOLUME, this.audio.volume);
    }
  };

  // UI Controller
  const UIController = {
    elements: {},

    init() {
      this.cacheElements();
      if (!this.elements.container) return;

      this.bindEvents();
      this.restoreState();
    },

    cacheElements() {
      this.elements.container = document.querySelector(CONFIG.SELECTORS.CONTAINER);
      if (!this.elements.container) return;

      this.elements.audio = this.elements.container.querySelector(CONFIG.SELECTORS.AUDIO);
      this.elements.playPauseBtn = this.elements.container.querySelector(CONFIG.SELECTORS.PLAY_PAUSE_BTN);
      this.elements.iconPlay = this.elements.container.querySelector(CONFIG.SELECTORS.ICON_PLAY);
      this.elements.iconPause = this.elements.container.querySelector(CONFIG.SELECTORS.ICON_PAUSE);
      this.elements.trackTitle = this.elements.container.querySelector(CONFIG.SELECTORS.TRACK_TITLE);
      this.elements.audioTrigger = this.elements.container.querySelector(CONFIG.SELECTORS.AUDIO_TRIGGER);
      this.elements.audioDropdown = this.elements.container.querySelector(CONFIG.SELECTORS.AUDIO_DROPDOWN);
      this.elements.trackOptions = this.elements.container.querySelectorAll(CONFIG.SELECTORS.TRACK_OPTION);
      this.elements.infoTrigger = this.elements.container.querySelector(CONFIG.SELECTORS.INFO_TRIGGER);
      this.elements.infoPanel = document.querySelector(CONFIG.SELECTORS.INFO_PANEL); 
      this.elements.infoContentContainer = document.querySelector(CONFIG.SELECTORS.INFO_CONTENT_CONTAINER);
      this.elements.locationLabel = this.elements.container.querySelector(CONFIG.SELECTORS.LOCATION_LABEL);
      this.elements.locationValue = this.elements.container.querySelector(CONFIG.SELECTORS.LOCATION_VALUE);
      this.elements.volumeToggle = this.elements.container.querySelector(CONFIG.SELECTORS.VOLUME_TOGGLE);
      this.elements.volumeTrigger = this.elements.container.querySelector(CONFIG.SELECTORS.VOLUME_TRIGGER);
      this.elements.volumePanel = this.elements.container.querySelector(CONFIG.SELECTORS.VOLUME_PANEL);
      this.elements.volumeSlider = this.elements.container.querySelector(CONFIG.SELECTORS.VOLUME_SLIDER);
      this.elements.volumeIconOn = this.elements.container.querySelector(CONFIG.SELECTORS.VOLUME_ICON_ON);
      this.elements.volumeIconOff = this.elements.container.querySelector(CONFIG.SELECTORS.VOLUME_ICON_OFF);
      
      // Toggle mode elements
      this.isToggleMode = !!this.elements.container.querySelector(CONFIG.SELECTORS.CONTAINER_TOGGLE);
      this.elements.toggleTracks = this.elements.container.querySelectorAll(CONFIG.SELECTORS.TOGGLE_TRACK);
      this.elements.togglePlayBtns = this.elements.container.querySelectorAll(CONFIG.SELECTORS.TOGGLE_PLAY);
      this.elements.toggleInfoTriggers = this.elements.container.querySelectorAll('[data-toggle-info-trigger]');
      this.elements.toggleInfoPanels = this.elements.container.querySelectorAll('[data-toggle-info-panel]');
      this.currentToggleIndex = 0;
      this.activeToggleInfoPanel = null;
    },

    bindEvents() {
      // Toggle Mode - bind toggle play buttons and info panel triggers
      if (this.isToggleMode) {
        this.elements.togglePlayBtns.forEach((btn, index) => {
          btn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.handleToggleClick(btn, index);
          });
        });

        // Toggle info panel triggers (caret buttons)
        this.elements.toggleInfoTriggers.forEach((trigger) => {
          trigger.addEventListener('click', (e) => {
            e.stopPropagation();
            const panelIndex = parseInt(trigger.dataset.toggleInfoIndex, 10);
            this.toggleToggleInfoPanel(panelIndex, trigger);
          });
        });
      } else {
        // Standard Mode - Play/Pause
        if (this.elements.playPauseBtn) {
          this.elements.playPauseBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            AudioController.toggle();
          });
        }

        // Audio Menu Toggle
        if (this.elements.audioTrigger && this.elements.audioDropdown) {
          this.elements.audioTrigger.addEventListener('click', (e) => {
            e.stopPropagation();
            this.toggleDropdown();
          });
        }

        // Track Selection
        this.elements.trackOptions.forEach(option => {
          option.addEventListener('click', (e) => {
            e.stopPropagation();
            this.selectTrack(option);
            this.closeDropdown();
          });
        });
      }

      // Info Panel Toggle (both modes)
      if (this.elements.infoTrigger && this.elements.infoPanel) {
        this.elements.infoTrigger.addEventListener('click', (e) => {
          e.stopPropagation();
          this.toggleInfoPanel();
        });
      }

      // Volume Control
      if (this.elements.volumeTrigger) {
        // Toggle mode: open/close fold-out panel
        this.elements.volumeTrigger.addEventListener('click', (e) => {
          e.stopPropagation();
          this.toggleVolumePanel();
        });
      } else if (this.elements.volumeToggle) {
        // Standard mode: mute/unmute
        this.elements.volumeToggle.addEventListener('click', (e) => {
          e.stopPropagation();
          this.toggleMute();
        });
      }

      if (this.elements.volumeSlider) {
        this.elements.volumeSlider.addEventListener('input', (e) => {
          const value = parseInt(e.target.value, 10) / 100;
          AudioController.setVolume(value);
          this.updateVolumeIcon(value);
        });
      }

      // Close dropdowns on outside click
      document.addEventListener('click', (e) => {
        // Standard mode dropdown
        if (this.elements.audioDropdown && this.elements.audioDropdown.classList.contains('is-open') && !this.elements.audioDropdown.contains(e.target) && !this.elements.audioTrigger.contains(e.target)) {
          this.closeDropdown();
        }

        // Toggle mode info panels
        if (this.isToggleMode && this.activeToggleInfoPanel !== null) {
          const panel = this.elements.toggleInfoPanels[this.activeToggleInfoPanel];
          const trigger = this.elements.toggleInfoTriggers[this.activeToggleInfoPanel];
          if (panel && !panel.contains(e.target) && trigger && !trigger.contains(e.target)) {
            this.closeToggleInfoPanel();
          }
        }

        // Volume panel (toggle mode)
        if (this.elements.volumePanel && this.elements.volumePanel.classList.contains('is-open')) {
          if (!this.elements.volumePanel.contains(e.target) &&
              (!this.elements.volumeTrigger || !this.elements.volumeTrigger.contains(e.target))) {
            this.closeVolumePanel();
          }
        }
        
        if (this.elements.infoPanel && this.elements.infoPanel.getAttribute('aria-hidden') === 'false' && !this.elements.infoPanel.contains(e.target) && !this.elements.infoTrigger.contains(e.target)) {
          this.closeInfoPanel();
        }
      });

      // Close on Escape
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.closeDropdown();
          this.closeToggleInfoPanel();
          this.closeInfoPanel();
          this.closeVolumePanel();
        }
      });
    },

    restoreState() {
      const state = StateManager.getAll();

      // Initialize Audio first
      if (this.elements.audio) {
        AudioController.init(this.elements.audio);
      }

      // Toggle Mode restoration
      if (this.isToggleMode) {
        // In toggle mode, check if we have a stored track ID and find its index
        let restoreIndex = 0;
        if (state.trackId) {
          this.elements.togglePlayBtns.forEach((btn, index) => {
            if (btn.dataset.trackId === state.trackId) {
              restoreIndex = index;
            }
          });
        }

        // Load the track
        const btn = this.elements.togglePlayBtns[restoreIndex];
        if (btn) {
          const trackUrl = btn.dataset.trackUrl;
          AudioController.load(trackUrl);
          this.currentToggleIndex = restoreIndex;
          this.updateToggleActiveState(restoreIndex);
          this.updateTogglePlayState(restoreIndex, false);

          // Attempt resume if previously playing
          if (state.isPlaying && state.userInitiated) {
            AudioController.playFromRandomPosition();
          }
        }

        // Restore volume slider position
        if (this.elements.volumeSlider) {
          const volumePercent = Math.round(state.volume * 100);
          this.elements.volumeSlider.value = volumePercent;
          this.updateVolumeIcon(state.volume);
        }
        return;
      }

      // Standard Mode restoration
      // Restore track info
      if (state.trackTitle) {
        this.updateTrackDisplay(state.trackTitle);
      }
      
      // Restore location
      if (state.trackLocationLabel !== null && state.trackLocationValue !== null) {
        this.updateLocationDisplay(state.trackLocationLabel, state.trackLocationValue);
      }

      // Restore volume slider
      if (this.elements.volumeSlider) {
        const volumePercent = Math.round(state.volume * 100);
        this.elements.volumeSlider.value = volumePercent;
        this.updateVolumeIcon(state.volume);
      }

      // Load track
      if (state.trackUrl) {
        AudioController.load(state.trackUrl);
        
        // Restore info panel content
        if (state.trackId) {
          this.updateInfoPanelContent(state.trackId);
        }

        // Attempt resume if previously playing and user initiated
        if (state.isPlaying && state.userInitiated) {
          AudioController.playFromRandomPosition();
        } else {
          // Reflect paused state
          this.updatePlayState(false);
        }
      } else {
        // If no track stored, select first track by default (without auto-playing)
        const firstOption = this.elements.trackOptions[0];
        if (firstOption) {
           // Simulate selection but don't play
           const trackId = firstOption.dataset.trackId;
           const trackUrl = firstOption.dataset.trackUrl;
           const trackTitle = firstOption.dataset.trackTitle;
           const trackLocationLabel = firstOption.dataset.trackLocationLabel;
           const trackLocationValue = firstOption.dataset.trackLocationValue;

           this.updateTrackDisplay(trackTitle);
           this.updateLocationDisplay(trackLocationLabel, trackLocationValue);
           this.updateInfoPanelContent(trackId);
           AudioController.load(trackUrl);
           
           // Don't save this as user initiated yet
        }
      }
    },

    updatePlayState(isPlaying) {
      // Toggle mode uses separate method
      if (this.isToggleMode) {
        this.updateTogglePlayState(this.currentToggleIndex, isPlaying);
        return;
      }

      // Standard mode
      if (this.elements.iconPlay && this.elements.iconPause) {
        if (isPlaying) {
          this.elements.iconPlay.classList.add('hidden');
          this.elements.iconPause.classList.remove('hidden');
          if (this.elements.playPauseBtn) this.elements.playPauseBtn.setAttribute('aria-label', 'Pause');
        } else {
          this.elements.iconPlay.classList.remove('hidden');
          this.elements.iconPause.classList.add('hidden');
          if (this.elements.playPauseBtn) this.elements.playPauseBtn.setAttribute('aria-label', 'Play');
        }
      }
    },

    // Toggle Mode Methods
    handleToggleClick(btn, index) {
      const trackId = btn.dataset.trackId;
      const trackUrl = btn.dataset.trackUrl;
      const isSameTrack = (this.currentToggleIndex === index);
      
      if (isSameTrack) {
        // Same track - toggle play/pause
        AudioController.toggle();
      } else {
        // Different track - switch and play
        this.currentToggleIndex = index;
        this.updateToggleActiveState(index);
        
        // Save state
        StateManager.set(CONFIG.STORAGE_KEYS.TRACK_ID, trackId);
        StateManager.set(CONFIG.STORAGE_KEYS.TRACK_URL, trackUrl);
        StateManager.set(CONFIG.STORAGE_KEYS.USER_INITIATED, true);
        
        // Load and play
        AudioController.load(trackUrl);
        AudioController.play();
      }
    },

    updateToggleActiveState(activeIndex) {
      this.elements.toggleTracks.forEach((track, index) => {
        if (index === activeIndex) {
          track.classList.add('is-active');
        } else {
          track.classList.remove('is-active');
          // Reset non-active track to show play icon
          const playIcon = track.querySelector(CONFIG.SELECTORS.ICON_PLAY);
          const pauseIcon = track.querySelector(CONFIG.SELECTORS.ICON_PAUSE);
          if (playIcon) playIcon.classList.remove('hidden');
          if (pauseIcon) pauseIcon.classList.add('hidden');
        }
      });
    },

    updateTogglePlayState(index, isPlaying) {
      const track = this.elements.toggleTracks[index];
      if (!track) return;

      const playIcon = track.querySelector(CONFIG.SELECTORS.ICON_PLAY);
      const pauseIcon = track.querySelector(CONFIG.SELECTORS.ICON_PAUSE);
      
      if (playIcon && pauseIcon) {
        if (isPlaying) {
          playIcon.classList.add('hidden');
          pauseIcon.classList.remove('hidden');
        } else {
          playIcon.classList.remove('hidden');
          pauseIcon.classList.add('hidden');
        }
      }
    },

    toggleToggleInfoPanel(panelIndex, trigger) {
      const panel = this.elements.toggleInfoPanels[panelIndex];
      if (!panel) return;

      const isHidden = panel.getAttribute('aria-hidden') === 'true';

      // Close any other open panel first
      this.closeToggleInfoPanel();

      if (isHidden) {
        panel.setAttribute('aria-hidden', 'false');
        trigger.setAttribute('aria-expanded', 'true');
        this.activeToggleInfoPanel = panelIndex;
      }
    },

    closeToggleInfoPanel() {
      if (this.activeToggleInfoPanel === null) return;

      const panel = this.elements.toggleInfoPanels[this.activeToggleInfoPanel];
      const trigger = this.elements.toggleInfoTriggers[this.activeToggleInfoPanel];

      if (panel) {
        panel.setAttribute('aria-hidden', 'true');
      }
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
      }

      this.activeToggleInfoPanel = null;
    },

    updateTrackDisplay(title) {
      if (this.elements.trackTitle) {
        this.elements.trackTitle.textContent = title;
      }
    },

    updateLocationDisplay(label, value) {
      if (this.elements.locationLabel) this.elements.locationLabel.textContent = label;
      if (this.elements.locationValue) this.elements.locationValue.textContent = value;
    },

    updateVolumeIcon(volume) {
      if (!this.elements.volumeIconOn || !this.elements.volumeIconOff) return;
      
      if (volume === 0) {
        this.elements.volumeIconOn.classList.add('hidden');
        this.elements.volumeIconOff.classList.remove('hidden');
      } else {
        this.elements.volumeIconOn.classList.remove('hidden');
        this.elements.volumeIconOff.classList.add('hidden');
      }
    },

    toggleMute() {
      if (!this.elements.volumeSlider) return;
      
      const currentVolume = parseInt(this.elements.volumeSlider.value, 10);
      
      if (currentVolume > 0) {
        // Store current volume before muting
        this._previousVolume = currentVolume;
        this.elements.volumeSlider.value = 0;
        AudioController.setVolume(0);
        this.updateVolumeIcon(0);
      } else {
        // Restore previous volume or default to 70
        const restoreVolume = this._previousVolume || 70;
        this.elements.volumeSlider.value = restoreVolume;
        AudioController.setVolume(restoreVolume / 100);
        this.updateVolumeIcon(restoreVolume / 100);
      }
    },

    toggleVolumePanel() {
      if (!this.elements.volumePanel) return;
      const isOpen = this.elements.volumePanel.classList.contains('is-open');
      if (isOpen) {
        this.closeVolumePanel();
      } else {
        this.openVolumePanel();
      }
    },

    openVolumePanel() {
      if (!this.elements.volumePanel) return;
      this.elements.volumePanel.classList.add('is-open');
      if (this.elements.volumeTrigger) {
        this.elements.volumeTrigger.setAttribute('aria-expanded', 'true');
      }
    },

    closeVolumePanel() {
      if (!this.elements.volumePanel) return;
      this.elements.volumePanel.classList.remove('is-open');
      if (this.elements.volumeTrigger) {
        this.elements.volumeTrigger.setAttribute('aria-expanded', 'false');
      }
    },

    updateInfoPanelContent(trackId) {
      if (!this.elements.infoContentContainer) return;
      
      const templateId = `info-template-${trackId}`;
      const template = document.getElementById(templateId);
      
      if (template) {
        // Clone and replace content
        const clone = template.content.cloneNode(true);
        this.elements.infoContentContainer.innerHTML = '';
        this.elements.infoContentContainer.appendChild(clone);
      } else {
        // Clear if no template found (shouldn't happen)
        this.elements.infoContentContainer.innerHTML = '';
      }
    },

    toggleDropdown() {
      const isOpen = this.elements.audioDropdown.classList.contains('is-open');
      
      if (isOpen) {
        this.closeDropdown();
      } else {
        this.elements.audioDropdown.hidden = false; // Ensure it's not display:none
        // Small delay to allow display:block to apply before adding class for transition
        requestAnimationFrame(() => {
          this.elements.audioDropdown.classList.add('is-open');
          this.elements.audioDropdown.setAttribute('aria-hidden', 'false');
          this.elements.audioTrigger.setAttribute('aria-expanded', 'true');
          
          // Clamp logic for viewport safety
          const rect = this.elements.audioDropdown.getBoundingClientRect();
          const viewportWidth = window.innerWidth;
          if (rect.right > viewportWidth) {
             this.elements.audioDropdown.style.right = '0';
             this.elements.audioDropdown.style.left = 'auto';
          }
        });
      }
    },

    closeDropdown() {
      if (this.elements.audioDropdown) {
        this.elements.audioDropdown.classList.remove('is-open');
        this.elements.audioDropdown.setAttribute('aria-hidden', 'true');
        this.elements.audioTrigger.setAttribute('aria-expanded', 'false');
        
        // Optional: set hidden=true after transition if we want to be strict, 
        // but CSS visibility:hidden handles the inert state.
      }
    },

    toggleInfoPanel() {
      const isHidden = this.elements.infoPanel.getAttribute('aria-hidden') === 'true';
      this.elements.infoPanel.setAttribute('aria-hidden', !isHidden);
      this.elements.infoTrigger.setAttribute('aria-expanded', isHidden);
    },

    closeInfoPanel() {
      if (this.elements.infoPanel) {
        this.elements.infoPanel.setAttribute('aria-hidden', 'true');
        this.elements.infoTrigger.setAttribute('aria-expanded', 'false');
      }
    },

    selectTrack(option) {
      const trackId = option.dataset.trackId;
      const trackUrl = option.dataset.trackUrl;
      const trackTitle = option.dataset.trackTitle;
      const trackLocationLabel = option.dataset.trackLocationLabel;
      const trackLocationValue = option.dataset.trackLocationValue;

      // Update State
      StateManager.set(CONFIG.STORAGE_KEYS.TRACK_ID, trackId);
      StateManager.set(CONFIG.STORAGE_KEYS.TRACK_URL, trackUrl);
      StateManager.set(CONFIG.STORAGE_KEYS.TRACK_TITLE, trackTitle);
      StateManager.set(CONFIG.STORAGE_KEYS.TRACK_LOCATION_LABEL, trackLocationLabel);
      StateManager.set(CONFIG.STORAGE_KEYS.TRACK_LOCATION_VALUE, trackLocationValue);

      // Update UI
      this.updateTrackDisplay(trackTitle);
      this.updateLocationDisplay(trackLocationLabel, trackLocationValue);
      this.updateInfoPanelContent(trackId);
      
      // Update Audio
      AudioController.load(trackUrl);
      
      // Auto-play on selection
      StateManager.set(CONFIG.STORAGE_KEYS.USER_INITIATED, true);
      AudioController.play();
    }
  };

  // Initialization
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => UIController.init());
  } else {
    UIController.init();
  }

})();
